import type {
    IDigitalDataComponent,
    IDigitalDataEvent,
    IDigitalDataPage,
    IAnalyticsMgr,
    IAnalyticsStrategy,
    IDigitalDataUser,
    IDigitalDataBaseEvent
} from '@nab/x-types';

/**
 * [[include:packages/consumer/sdk-browser/src/AnalyticsMgr/README.md]]
 */

class AnalyticsMgr implements IAnalyticsMgr {
    private analyticsStrategy: IAnalyticsStrategy;

    /**
     * @param {IAnalyticsStrategy} strategy Initialize the Analytics Manager to use the specific analytics strategy that will help capture the data from the manager to required analytic solution.
     * @param {IDigitalDataPage} rootPageInfo Analytics manager will always require to initialize and capture the root page information to start analytic on.
     */
    constructor(strategy: IAnalyticsStrategy) {
        if (!strategy) {
            throw new Error('Missing Analytics strategy, Please initialise one and pass through.');
        }
        this.analyticsStrategy = strategy;
    }

    /**
     * Sets the page information on the analytics strategy, where the analytics manager is initialized.
     * @param pageData initial Page Data on which the analytics manager was initialized
     */
    public initPage(pageData: IDigitalDataPage): Promise<boolean> {
        return this.analyticsStrategy.initPage(pageData);
    }

    /**
     * Used to update the page information on the strategy. This is useful in case shell has routing enabled and the
     * mini-apps are navigating or being loaded on different paths.
     * @param pageData Data to be updated on the page object, this can be partial data that needs to be updated
     */
    public updatePage(pageData: Partial<IDigitalDataPage>): Promise<boolean> {
        return this.analyticsStrategy.updatePage(pageData);
    }

    /**
     * Used to capture any analytics events within the strategy passed.
     * @param eventData data to be captured for the event occurred. it must have an eventType hence the extension from IDigitalDataBaseEvent
     */
    public logEvent<T extends IDigitalDataBaseEvent = IDigitalDataEvent>(eventData: T): Promise<boolean> {
        return this.analyticsStrategy.logEvent<T>(eventData);
    }

    /**
     * Used to log the data for the component (mini-app) being loaded or mounted on the shell.
     * @param componentData component (mini-app) data being loaded or mounted.
     */
    public logComponent(componentData: IDigitalDataComponent): Promise<boolean> {
        return this.analyticsStrategy.logComponent(componentData);
    }

    /**
     * Sets the user information on the analytics strategy, providsed the analytics manager is initialized.
     * @param userData initial user data
     */
    public initUser(userData: IDigitalDataUser): Promise<boolean> {
        return this.analyticsStrategy.initUser(userData);
    }

    /**
     * Used to update the user information on the strategy. This is useful in case shell any user information changes along the way for e.g.
     * user logged in status.
     * @param userData Data to be updated on the user object, this can be partial data that needs to be updated
     */
    public updateUser(userData: Partial<IDigitalDataUser>): Promise<boolean> {
        return this.analyticsStrategy.updateUser(userData);
    }
}

export { AnalyticsMgr };
